import { SKU } from "@/data/mockData";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { formatCurrency } from "@/lib/utils";
import { 
  AlertTriangle, 
  XCircle, 
  Package,
  ShoppingCart,
  Clock,
  TrendingDown
} from "lucide-react";

interface StockAlertProps {
  sku: SKU;
  onReorder?: (skuId: number) => void;
  showActions?: boolean;
}

export const StockAlert = ({ sku, onReorder, showActions = true }: StockAlertProps) => {
  const isOutOfStock = sku.currentStock <= 0;
  const isLowStock = sku.currentStock > 0 && sku.currentStock <= sku.reorderLevel;
  const isWarning = sku.currentStock > sku.reorderLevel && sku.currentStock <= sku.reorderLevel * 1.5;

  if (!isOutOfStock && !isLowStock && !isWarning) {
    return null;
  }

  const getAlertConfig = () => {
    if (isOutOfStock) {
      return {
        variant: "destructive" as const,
        icon: XCircle,
        title: "Out of Stock",
        description: "This item is completely out of stock and needs immediate attention.",
        badgeVariant: "destructive" as const,
        priority: "Critical"
      };
    }
    
    if (isLowStock) {
      return {
        variant: "destructive" as const,
        icon: AlertTriangle,
        title: "Low Stock Alert",
        description: `Stock level (${sku.currentStock}) is at or below the reorder level (${sku.reorderLevel}).`,
        badgeVariant: "destructive" as const,
        priority: "High"
      };
    }
    
    return {
      variant: "default" as const,
      icon: AlertTriangle,
      title: "Stock Warning",
      description: `Stock level is getting low. Consider reordering soon.`,
      badgeVariant: "secondary" as const,
      priority: "Medium"
    };
  };

  const config = getAlertConfig();
  const Icon = config.icon;

  // Calculate days until stock runs out (rough estimate)
  const estimatedDaysRemaining = () => {
    // This is a simplified calculation - in a real system you'd use historical consumption data
    const averageDailyUsage = Math.max(1, Math.floor(sku.reorderLevel / 30)); // Rough estimate
    return Math.floor(sku.currentStock / averageDailyUsage);
  };

  const daysRemaining = estimatedDaysRemaining();

  // Get preferred vendor for reordering
  const preferredVendor = sku.linkedVendors.find(v => v.isPreferred) || sku.linkedVendors[0];

  return (
    <Alert variant={config.variant} className="mb-4">
      <Icon className="h-4 w-4" />
      <AlertTitle className="flex items-center gap-2">
        {config.title}
        <Badge variant={config.badgeVariant} className="text-xs">
          {config.priority}
        </Badge>
      </AlertTitle>
      <AlertDescription className="space-y-3">
        <div>
          {config.description}
        </div>
        
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
          <div className="flex items-center gap-2">
            <Package className="h-3 w-3" />
            <span>Current: {sku.currentStock.toLocaleString()} {sku.unit}</span>
          </div>
          <div className="flex items-center gap-2">
            <TrendingDown className="h-3 w-3" />
            <span>Reorder at: {sku.reorderLevel.toLocaleString()} {sku.unit}</span>
          </div>
          {!isOutOfStock && (
            <div className="flex items-center gap-2">
              <Clock className="h-3 w-3" />
              <span>~{daysRemaining} days remaining</span>
            </div>
          )}
        </div>

        {preferredVendor && (
          <div className="text-sm bg-muted/50 p-2 rounded">
            <strong>Reorder Info:</strong> Min. order {preferredVendor.minimumOrderQty.toLocaleString()} {sku.unit} 
            at {formatCurrency(preferredVendor.defaultPrice)} each 
            ({preferredVendor.leadTime} days lead time)
          </div>
        )}

        {showActions && (
          <div className="flex gap-2 pt-2">
            <Button 
              size="sm" 
              onClick={() => onReorder?.(sku.id)}
              className="flex items-center gap-1"
            >
              <ShoppingCart className="h-3 w-3" />
              Create Purchase Order
            </Button>
            <Button size="sm" variant="outline">
              View Details
            </Button>
          </div>
        )}
      </AlertDescription>
    </Alert>
  );
};
